import { cn } from '@invent/wl-ui-kit-next';
import { Button, Input } from '@invent/wl-ui-kit-next/ui';
import { ArrowDown, ArrowUp, Lock } from 'lucide-react';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { DataTable, StatusChip, type ChipTone, type IDataColumn } from '@/design';
import { DemoSelect, MockPanel, NotBuilt } from '../chrome/marks';
import { MockScreen, ViewOnlyNote, useScreenGrant } from '../chrome/mock-screen';
import {
  DEMO_CYCLE,
  SCORED_RESULT_FILE,
  firmName,
  personName,
  type IDemoPerson,
  type IRankingList
} from '../fixtures';
import { day, millions } from '../format';
import type { IRankingChange } from '../state/act4';
import { useActingAs, useDemo } from '../state/demo-state';
import {
  RANKING_FAMILY_LABEL,
  RANKING_FAMILY_SURVEY,
  SURVEY_TYPE_LABEL,
  qualityRatingLabel,
  type Eligibility
} from '../vocabulary';

/**
 * Rankings (step 20) — WF-12: the lists the external scoring result placed, changed by hand, sent
 * to the firms as finalists, and published.
 *
 * **The platform does not rank.** The algorithm runs outside it (RNK-17) and its result is imported
 * as a file; this screen starts from that file's order and says where it came from, so no one reads
 * a place as something the portal computed.
 *
 * **A place is a position, never a stored number.** A list is an order of people; remove one and
 * everyone below moves up one, insert one at place N and everyone from N moves down one (RNK-14).
 * That renumbering is the whole point of the screen, so it is made visible rather than silent: the
 * rows whose place changed are lit for two seconds with their old place struck beside the new, and
 * a status line says who moved and how far. A list that silently renumbered would leave an analyst
 * checking every row below each edit by hand — the work this replaces.
 *
 * **Every change is logged with its optional note** (who, when, what place), per list. **Publish is
 * one confirmed, permanent press**: it freezes every list — no edit control is drawn after it. It
 * wrote each placement onto the demo's cast for the worklists mock's pane until SR-230 deleted that
 * mock; it writes nothing now. No route undoes it, so the screen does not offer to.
 *
 * The list on screen is the URL's `?list=`, so a list can be linked to and survives a reload of
 * the address (not of the demo's state, which a reload resets).
 */

const MISSING =
  'Nothing stores a ranking: the scored result’s import, the lists and their changes, the finalist exchange and the publish that writes placements have no route yet.';

const ELIGIBILITY_TONE: Record<Eligibility, ChipTone> = {
  Eligible: 'success',
  'Not eligible': 'attention',
  'Under review': 'info',
  'Not yet reviewed': 'neutral'
};

/** How long a renumbered row stays lit. */
const FLASH_MS = 2000;

function listTitle(list: IRankingList): string {
  return `${RANKING_FAMILY_LABEL[list.family]} — ${list.territory}`;
}

function movedSentence(count: number, direction: 'up' | 'down'): string {
  if (count === 0) return 'nobody was below.';
  return `${count} advisor${count === 1 ? '' : 's'} moved ${direction} one.`;
}

export function RankingsPage() {
  const { state } = useDemo();
  const [params] = useSearchParams();
  const { lists, published } = state.act4.rankings;
  const list = lists.find((entry) => entry.id === params.get('list')) ?? lists[0];

  return (
    <MockScreen
      screen="rankings"
      title="Rankings"
      lede="The scoring result’s placements, per list. Remove or insert, and everyone below renumbers; publish when final."
      missing={MISSING}
      actions={
        published ? (
          <StatusChip tone="success" dot>
            Published {day(published.at)}
          </StatusChip>
        ) : (
          <StatusChip tone="info" dot>
            Not published — placements can change
          </StatusChip>
        )
      }
    >
      {published && <Published />}
      <ScoredResult />
      {list ? (
        <div className="grid gap-6 xl:grid-cols-[17rem_minmax(0,1fr)]">
          <ListPicker lists={lists} current={list.id} />
          <div className="grid min-w-0 content-start gap-6">
            <Placements key={list.id} list={list} />
            <ChangeLog list={list} />
          </div>
        </div>
      ) : (
        <p className="text-[0.8125rem] text-muted-foreground">The scoring result holds no list.</p>
      )}
      <div className="grid gap-6 xl:grid-cols-2">
        <Finalists />
        {!published && <Publish />}
      </div>
    </MockScreen>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Where the placements came from
 * ---------------------------------------------------------------------------------------------- */

function ScoredResult() {
  const { state } = useDemo();
  const { lists, changes } = state.act4.rankings;
  const placed = lists.reduce((sum, list) => sum + list.order.length, 0);
  return (
    <MockPanel
      title="Scoring result"
      description={
        <>
          Imported from <code className="text-[var(--color-royal-blue)]">{SCORED_RESULT_FILE.name}</code> at{' '}
          {SCORED_RESULT_FILE.importedAt} by {SCORED_RESULT_FILE.by}.
        </>
      }
      actions={<NotBuilt>Import a new scored result</NotBuilt>}
    >
      <div className="grid gap-3 text-[0.8125rem]">
        <p>
          The ranking algorithm runs outside the platform (RNK-17): this file is its output, and every
          list below starts in the order it gave. What changes here is recorded as a change to that order.
        </p>
        <dl className="flex flex-wrap gap-x-10 gap-y-2">
          <Figure label="Lists" value={lists.length} />
          <Figure label="Placements" value={placed} />
          <Figure label="Changes since the import" value={changes.length} />
        </dl>
      </div>
    </MockPanel>
  );
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div className="grid">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-xl font-medium tabular-nums text-[var(--color-royal-blue)]">{value}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * The lists
 * ---------------------------------------------------------------------------------------------- */

function ListPicker({ lists, current }: { lists: IRankingList[]; current: string }) {
  const { state } = useDemo();
  const baseId = React.useId();
  return (
    <MockPanel title="Lists" description="One per family and territory." className="h-fit">
      <ul className="-mx-1 grid gap-0.5">
        {lists.map((list) => {
          const open = list.id === current;
          const edits = state.act4.rankings.changes.filter((change) => change.listId === list.id).length;
          const countId = `${baseId}-${list.id}`;
          return (
            <li key={list.id}>
              <Link
                to={{ search: `?list=${encodeURIComponent(list.id)}` }}
                replace
                aria-current={open ? 'true' : undefined}
                aria-label={listTitle(list)}
                aria-describedby={countId}
                className={cn(
                  'grid gap-0.5 rounded-md px-2.5 py-2 text-[0.8125rem] outline-none hover:bg-[var(--accent)] focus-visible:ring-2 focus-visible:ring-ring',
                  open && 'bg-[color-mix(in_srgb,var(--color-teal)_8%,white)] shadow-[inset_3px_0_0_var(--color-teal)]'
                )}
              >
                <span className="font-medium text-[var(--color-royal-blue)]">{RANKING_FAMILY_LABEL[list.family]}</span>
                <span className="flex flex-wrap items-center justify-between gap-x-2 text-muted-foreground">
                  <span>{list.territory}</span>
                  <span id={countId} className="tabular-nums">
                    {list.order.length} placed{edits > 0 ? ` · ${edits} change${edits === 1 ? '' : 's'}` : ''}
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </MockPanel>
  );
}

interface IPlacementRow {
  place: number;
  entry: IDemoPerson;
}

interface IFlash {
  /** Person id → the place they held before the change. */
  was: Record<string, number>;
  added: Nullable<string>;
}

function Placements({ list }: { list: IRankingList }) {
  const { state, dispatch } = useDemo();
  const { acts } = useScreenGrant();
  const acting = useActingAs();
  const frozen = state.act4.rankings.published !== null;
  const people = new Map(state.act2.people.map((entry) => [entry.id, entry]));
  const [flash, setFlash] = React.useState<Nullable<IFlash>>(null);
  const [said, setSaid] = React.useState('');
  const [removing, setRemoving] = React.useState<Nullable<string>>(null);
  const [inserting, setInserting] = React.useState(false);

  React.useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flash]);

  const rows: IPlacementRow[] = list.order.flatMap((id, index) => {
    const entry = people.get(id);
    return entry ? [{ place: index + 1, entry }] : [];
  });

  const remove = (personId: string, note: string) => {
    const index = list.order.indexOf(personId);
    if (index < 0) return;
    const below = list.order.slice(index + 1);
    dispatch({ type: 'ranking/remove', listId: list.id, personId, note, by: acting.name });
    setFlash({ was: Object.fromEntries(below.map((id, offset) => [id, index + 2 + offset])), added: null });
    setSaid(`${personName(personId)} removed from place ${index + 1}; ${movedSentence(below.length, 'up')}`);
    setRemoving(null);
  };

  const insert = (personId: string, place: number, note: string) => {
    const index = Math.min(Math.max(place, 1), list.order.length + 1) - 1;
    const below = list.order.slice(index);
    dispatch({ type: 'ranking/insert', listId: list.id, personId, place: index + 1, note, by: acting.name });
    setFlash({ was: Object.fromEntries(below.map((id, offset) => [id, index + 1 + offset])), added: personId });
    setSaid(`${personName(personId)} inserted at place ${index + 1}; ${movedSentence(below.length, 'down')}`);
    setInserting(false);
  };

  const columns: IDataColumn<IPlacementRow>[] = [
    {
      id: 'place',
      header: 'Place',
      className: 'w-24',
      cell: ({ place, entry }) => {
        const was = flash?.was[entry.id];
        return (
          <span className="flex items-baseline gap-2">
            <span className="text-2xl leading-none font-medium tabular-nums text-[var(--color-royal-blue)]">{place}</span>
            {was !== undefined && (
              <span className="inline-flex items-center gap-0.5 text-xs whitespace-nowrap text-[var(--color-teal)]">
                {was > place ? <ArrowUp className="size-3" aria-hidden /> : <ArrowDown className="size-3" aria-hidden />}
                <s className="tabular-nums">was {was}</s>
              </span>
            )}
            {flash?.added === entry.id && <StatusChip tone="info">Inserted</StatusChip>}
          </span>
        );
      }
    },
    {
      id: 'advisor',
      header: 'Advisor',
      cell: ({ entry }) => (
        <span className="grid gap-0.5">
          <span className="font-medium">{entry.name}</span>
          <span className="text-xs text-muted-foreground">
            {entry.idType} <span className="tabular-nums">{entry.number}</span> · {entry.city}, {entry.state}
          </span>
        </span>
      )
    },
    {
      id: 'firm',
      header: 'Firm',
      cell: ({ entry }) => (
        <span>{firmName(entry.firmId)}</span>
      )
    },
    { id: 'aum', header: 'AUM', numeric: true, cell: ({ entry }) => millions(entry.aum) },
    { id: 'quality', header: 'Quality rating', cell: ({ entry }) => qualityRatingLabel(entry.quality) },
    {
      id: 'eligibility',
      header: 'Eligibility',
      cell: ({ entry }) => <StatusChip tone={ELIGIBILITY_TONE[entry.eligibility]}>{entry.eligibility}</StatusChip>
    },
    ...(frozen
      ? []
      : [
          {
            id: 'change',
            header: <span className="sr-only">Change</span>,
            className: 'w-px whitespace-nowrap',
            cell: ({ place, entry }: IPlacementRow) =>
              removing === entry.id ? (
                <RemoveForm
                  name={entry.name}
                  place={place}
                  onRemove={(note) => remove(entry.id, note)}
                  onCancel={() => setRemoving(null)}
                />
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!acts}
                  onClick={() => {
                    setRemoving(entry.id);
                    setInserting(false);
                  }}
                >
                  Remove<span className="sr-only"> {entry.name}</span>
                </Button>
              )
          }
        ])
  ];

  return (
    <MockPanel
      title={listTitle(list)}
      source="no-endpoint"
      description={`${rows.length} placed, from the ${SURVEY_TYPE_LABEL[RANKING_FAMILY_SURVEY[list.family]]} survey’s records. ${
        frozen ? 'Published — this order is final.' : 'A place is a position in the list: change one and everyone below renumbers.'
      }`}
      actions={
        frozen ? (
          <StatusChip tone="neutral">
            <Lock className="size-3" aria-hidden /> Frozen
          </StatusChip>
        ) : (
          <Button
            type="button"
            variant={inserting ? 'secondary' : 'default'}
            aria-expanded={inserting}
            disabled={!acts}
            onClick={() => {
              setInserting((open) => !open);
              setRemoving(null);
            }}
          >
            Insert an advisor
          </Button>
        )
      }
    >
      <div className="grid gap-3 text-[0.8125rem]">
        {inserting && !frozen && (
          <InsertForm list={list} onInsert={insert} onCancel={() => setInserting(false)} />
        )}
        <p role="status" aria-live="polite" className="min-h-5 font-medium text-[var(--color-teal)]">
          {said}
        </p>
        <DataTable
          label="Placements"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.entry.id}
          maxHeight="none"
          minWidth="52rem"
          rowProps={({ place, entry }) =>
            ({
              'data-place': String(place),
              'data-person': entry.id,
              className: cn(
                'transition-colors duration-700',
                flash?.added === entry.id
                  ? 'bg-[color-mix(in_srgb,var(--color-turquoise)_22%,white)]'
                  : flash && flash.was[entry.id] !== undefined
                    ? 'bg-[color-mix(in_srgb,var(--color-turquoise)_12%,white)]'
                    : undefined
              )
            }) as React.HTMLAttributes<HTMLTableRowElement>
          }
          state={{ kind: 'empty', content: 'Nobody is placed on this list — insert an advisor to start it.' }}
        />
        <ViewOnlyNote what="changing a placement" />
      </div>
    </MockPanel>
  );
}

function RemoveForm({
  name,
  place,
  onRemove,
  onCancel
}: {
  name: string;
  place: number;
  onRemove: (note: string) => void;
  onCancel: () => void;
}) {
  const [note, setNote] = React.useState('');
  return (
    <form
      aria-label={`Remove ${name}`}
      onSubmit={(event) => {
        event.preventDefault();
        onRemove(note.trim());
      }}
      className="flex flex-wrap items-center gap-2"
    >
      <Input
        autoFocus
        aria-label={`Context note for removing ${name} (optional)`}
        placeholder="Context note (optional)"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel();
        }}
        className="h-8 w-56"
      />
      <Button type="submit" size="sm">
        Remove from place {place}
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}

function InsertForm({
  list,
  onInsert,
  onCancel
}: {
  list: IRankingList;
  onInsert: (personId: string, place: number, note: string) => void;
  onCancel: () => void;
}) {
  const { state } = useDemo();
  const survey = RANKING_FAMILY_SURVEY[list.family];
  const candidates = state.act2.people.filter((entry) => entry.survey === survey && !list.order.includes(entry.id));
  const last = list.order.length + 1;
  const [personId, setPersonId] = React.useState(candidates[0]?.id ?? '');
  const [place, setPlace] = React.useState(String(last));
  const [note, setNote] = React.useState('');
  const chosen = candidates.find((entry) => entry.id === personId);
  const number = Number(place);
  const valid = Number.isInteger(number) && number >= 1 && number <= last;

  if (candidates.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3">
        <p>Every {SURVEY_TYPE_LABEL[survey]} advisor in the demo’s cast is already on this list.</p>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <form
      aria-label="Insert an advisor"
      onSubmit={(event) => {
        event.preventDefault();
        if (chosen && valid) onInsert(chosen.id, number, note.trim());
      }}
      className="grid gap-3 rounded-md border border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-teal)_4%,white)] p-3"
    >
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_7rem_minmax(0,1fr)]">
        <label className="grid gap-1">
          Advisor
          <DemoSelect value={personId} onChange={(event) => setPersonId(event.target.value)} className="h-8">
            {candidates.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name} — {firmName(entry.firmId)} ({entry.eligibility})
              </option>
            ))}
          </DemoSelect>
        </label>
        <label className="grid gap-1">
          Place
          <Input
            type="number"
            min={1}
            max={last}
            value={place}
            onChange={(event) => setPlace(event.target.value)}
            aria-invalid={!valid}
            className="h-8 tabular-nums"
          />
        </label>
        <label className="grid gap-1">
          Context note (optional)
          <Input value={note} onChange={(event) => setNote(event.target.value)} className="h-8" />
        </label>
      </div>
      <p className="text-muted-foreground">
        {valid
          ? number === last
            ? `At the end of the list, place ${last}; nobody moves.`
            : `Everyone from place ${number} down moves down one.`
          : `A place from 1 to ${last}.`}
        {chosen && chosen.eligibility !== 'Eligible' && ` ${chosen.name} is ${chosen.eligibility.toLowerCase()} — inserted anyway if you go on.`}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={!chosen || !valid}>
          {chosen && valid ? `Insert ${chosen.name} at place ${number}` : 'Insert'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function ChangeLog({ list }: { list: IRankingList }) {
  const { state } = useDemo();
  const rows = state.act4.rankings.changes.filter((change) => change.listId === list.id).reverse();
  const columns: IDataColumn<IRankingChange>[] = [
    {
      id: 'kind',
      header: 'Change',
      cell: (change) => (
        <StatusChip tone={change.kind === 'removed' ? 'attention' : 'info'}>
          {change.kind === 'removed' ? 'Removed' : 'Inserted'}
        </StatusChip>
      )
    },
    { id: 'advisor', header: 'Advisor', cell: (change) => personName(change.personId) },
    {
      id: 'place',
      header: 'Place',
      cell: (change) => (
        <span className="tabular-nums">{change.kind === 'removed' ? `from ${change.place}` : `at ${change.place}`}</span>
      )
    },
    {
      id: 'note',
      header: 'Note',
      cell: (change) => change.note || <span className="text-muted-foreground">No note</span>
    },
    { id: 'by', header: 'By', cell: (change) => change.by },
    { id: 'at', header: 'Date', cell: (change) => day(change.at) }
  ];
  return (
    <MockPanel
      title="Change log"
      source="no-endpoint"
      description={`Every remove and insert on ${listTitle(list)}, newest first.`}
    >
      <DataTable
        label="Change log"
        columns={columns}
        rows={rows}
        rowKey={(change) => `${change.kind}-${change.personId}-${change.place}-${rows.indexOf(change)}`}
        maxHeight="none"
        state={{ kind: 'empty', content: 'No change since the import — this list is in the scoring result’s order.' }}
      />
    </MockPanel>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Finalists, and publishing
 * ---------------------------------------------------------------------------------------------- */

interface IFinalistFirm {
  firmId: string;
  people: string[];
  placements: number;
}

function Finalists() {
  const { state, dispatch } = useDemo();
  const { acts } = useScreenGrant();
  const { lists, finalistsSent, published } = state.act4.rankings;
  const [said, setSaid] = React.useState('');
  const people = new Map(state.act2.people.map((entry) => [entry.id, entry]));

  const byFirm = new Map<string, IFinalistFirm>();
  for (const list of lists) {
    for (const id of list.order) {
      const firmId = people.get(id)?.firmId;
      if (!firmId) continue;
      const row = byFirm.get(firmId) ?? { firmId, people: [], placements: 0 };
      if (!row.people.includes(id)) row.people.push(id);
      row.placements += 1;
      byFirm.set(firmId, row);
    }
  }
  const rows = [...byFirm.values()].sort((a, b) => firmName(a.firmId).localeCompare(firmName(b.firmId)));

  const columns: IDataColumn<IFinalistFirm>[] = [
    {
      id: 'firm',
      header: 'Firm',
      cell: (row) => (
        <span>{firmName(row.firmId)}</span>
      )
    },
    { id: 'finalists', header: 'Finalists', cell: (row) => row.people.map(personName).join(', ') },
    { id: 'placements', header: 'Placements', numeric: true, cell: (row) => row.placements },
    {
      id: 'sent',
      header: 'Sent',
      cell: (row) =>
        finalistsSent[row.firmId] ? day(finalistsSent[row.firmId]) : <StatusChip tone="neutral">Not sent</StatusChip>
    }
  ];

  const send = () => {
    const firmIds = rows.map((row) => row.firmId);
    dispatch({ type: 'ranking/send-finalists', firmIds });
    setSaid(`Finalist lists sent to ${firmIds.length} firm${firmIds.length === 1 ? '' : 's'}.`);
  };

  return (
    <MockPanel
      title="Finalists to firms"
      source="no-endpoint"
      description="Each firm gets its own advisors’ placements across every list, as they stand when sent — send again after a change."
    >
      <div className="grid gap-3 text-[0.8125rem]">
        <DataTable
          label="Finalists to firms"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.firmId}
          maxHeight="none"
          state={{ kind: 'empty', content: 'Nobody is placed, so no firm has a finalist.' }}
        />
        {!published && (
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" disabled={!acts || rows.length === 0} onClick={send}>
              Send finalist lists to firms
            </Button>
            <p role="status" className="text-[var(--color-teal)]">
              {said}
            </p>
          </div>
        )}
        <ViewOnlyNote what="sending finalist lists" />
      </div>
    </MockPanel>
  );
}

function Publish() {
  const { state, dispatch } = useDemo();
  const { acts } = useScreenGrant();
  const acting = useActingAs();
  const [confirming, setConfirming] = React.useState(false);
  const { lists } = state.act4.rankings;
  const placed = lists.reduce((sum, list) => sum + list.order.length, 0);

  return (
    <MockPanel
      title="Publish"
      source="no-endpoint"
      description="Makes every placement permanent and writes it to its advisor’s record. Nothing changes a list after it, and no route undoes it."
      className="h-fit"
    >
      <div className="grid gap-3 text-[0.8125rem]">
        {confirming ? (
          <div
            role="group"
            aria-label="Confirm publishing"
            className="grid gap-3 rounded-md border border-[var(--color-deep-magenta)] bg-[color-mix(in_srgb,var(--color-magenta)_5%,white)] p-3"
          >
            <p>
              Publish <strong className="tabular-nums">{placed}</strong> placements across{' '}
              <strong className="tabular-nums">{lists.length}</strong> lists for {DEMO_CYCLE}, as{' '}
              {acting.name}? Every remove, insert and send stops here, and each placement is written to its
              advisor’s ranking history.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                onClick={() => dispatch({ type: 'ranking/publish', by: acting.name })}
              >
                Publish permanently
              </Button>
              <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button type="button" className="w-fit" disabled={!acts} onClick={() => setConfirming(true)}>
            Publish the rankings
          </Button>
        )}
        <ViewOnlyNote what="publishing" />
      </div>
    </MockPanel>
  );
}

function Published() {
  const { state } = useDemo();
  const { published, lists } = state.act4.rankings;
  if (!published) return null;
  const placed = lists.reduce((sum, list) => sum + list.order.length, 0);
  const advisors = new Set(lists.flatMap((list) => list.order)).size;
  return (
    <MockPanel
      title="Published"
      source="no-endpoint"
      description={`${day(published.at)}, by ${published.by}. The placements are permanent.`}
    >
      <div className="grid gap-2 text-[0.8125rem]">
        <p>
          <strong className="tabular-nums">{placed}</strong> placements across{' '}
          <strong className="tabular-nums">{lists.length}</strong> lists, for{' '}
          <strong className="tabular-nums">{advisors}</strong> advisors, are published for {DEMO_CYCLE}.
          No list takes a remove, an insert or a send now.
        </p>
        <p>
          This publish does not write the person hub’s Rankings, which are real data.
        </p>
      </div>
    </MockPanel>
  );
}
